import type { LibrarySourceType } from "@/lib/library";

const LABEL_BY_TYPE: Record<NonNullable<LibrarySourceType>, string> = {
  book: "BOOK",
  journal_article: "ARTICLE",
  chapter: "CHAPTER",
  proceedings_article: "PAPER",
  website: "WEBSITE",
  report: "REPORT",
  podcast: "PODCAST",
  other: "SOURCE",
};

export interface LibrarySourceCoverPlaceholderProps {
  sourceType: LibrarySourceType | null | undefined;
  size?: "sm" | "md";
  className?: string;
}

/**
 * Themed placeholder shown in the cover slot for library items that don't
 * carry a real cover image (typically articles, chapters, papers, websites).
 *
 * Renders the actual Excerptus brandmark (CSS-masked /excerptus-logo.png
 * tinted with bg-primary, matching MarketingNav and AppShell) above a
 * small-caps source-type label. Works in light and dark mode via semantic
 * tokens; no asset request beyond the shared brandmark.
 */
export const LibrarySourceCoverPlaceholder = ({
  sourceType,
  size = "sm",
  className,
}: LibrarySourceCoverPlaceholderProps) => {
  const label = (sourceType && LABEL_BY_TYPE[sourceType]) || LABEL_BY_TYPE.other;

  const isLg = size === "md";
  const markSize = isLg ? "h-14 w-14" : "h-10 w-10";
  const labelSize = isLg ? "text-[10px]" : "text-[9px]";

  return (
    <div
      role="img"
      aria-label={`${label.toLowerCase()} placeholder cover`}
      className={`flex h-full w-full flex-col items-center justify-center gap-2 bg-muted text-foreground ${className ?? ""}`}
    >
      <span
        aria-hidden="true"
        className={`${markSize} bg-primary inline-block opacity-40`}
        style={{
          WebkitMaskImage: "url(/excerptus-logo.webp)",
          maskImage: "url(/excerptus-logo.webp)",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskPosition: "center",
          maskPosition: "center",
        }}
      />
      <span className={`${labelSize} font-semibold uppercase tracking-[0.18em] text-foreground/80`}>
        {label}
      </span>
    </div>
  );
};
